import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import Select from "../../../../../parts/inputs/select";
import {
  convertMeasurement,
  customRound,
  volumeUnits,
  weightUnits,
} from "../../../../../utils/nutrition-util";

export const HandleFoodModal = ({ locals }) => {
  const { query, mostUsedMealPlans, foodsByCoach } = locals;
  const allFoods = foodsByCoach || mostUsedMealPlans["foods"];

  const { foodId } = query;
  const currentFood = allFoods.find(({ _id }) => _id === foodId) || {
    name: "",
    brand: "",
    fullQuantity: "",
    quantityNumber: 0,
    quantityDescription: "",
    measurementsValue: 0,
    unitOfMeasure: "g",
    carbs: 0,
    proteins: 0,
    fats: 0,
    fibers: 0,
    calories: 0,
  };
  const {
    name: currentFoodName,
    brand: currentFoodBrand,
    quantityNumber: currentFoodQuantityNumber,
    quantityDescription: currentFoodQuantityDescription,
    measurementsValue: currentFoodMeasurementsValue,
    unitOfMeasure: currentFoodUnitOfMeasure,
    carbs: currentFoodCarbs,
    proteins: currentFoodProteins,
    fats: currentFoodFats,
    fibers: currentFoodFibers,
    calories: currentFoodCalories,
  } = currentFood;
  const title = foodId ? "Edit Food" : "Add Food";
  const unitOptions = [...weightUnits, ...volumeUnits]; //grams, ounces, pounds, milliliters, teaspoon, tablespoon, fluid ounce and cup
  const unitOptionsToUse = !foodId
    ? unitOptions
    : weightUnits.includes(currentFoodUnitOfMeasure)
    ? weightUnits
    : volumeUnits;
  const regex = /^(\d+\/\d+|\d+\.\d+|\d+)\s+(.+)$/;
  const initialState = {
    name: currentFoodName || "",
    brand: currentFoodBrand || "",
    fullQuantity:
      currentFoodQuantityNumber && currentFoodQuantityDescription
        ? `${currentFoodQuantityNumber} ${currentFoodQuantityDescription}`
        : "",
    quantityNumber: currentFoodQuantityNumber || 0,
    quantityDescription: currentFoodQuantityDescription || "",
    measurementsValue: currentFoodMeasurementsValue || 0,
    unitOfMeasure: currentFoodUnitOfMeasure || "g",
    carbs: currentFoodCarbs || 0,
    proteins: currentFoodProteins || 0,
    fats: currentFoodFats || 0,
    fibers: currentFoodFibers || 0,
    calories: currentFoodCalories || 0,
  };
  const [formState, setFormState] = useState(initialState);
  const [errorQtyMessage, setErrorQtyMessage] = useState("");

  const {
    name,
    brand,
    fullQuantity,
    measurementsValue,
    unitOfMeasure,
    carbs,
    proteins,
    fats,
    calories,
    quantityNumber,
    quantityDescription,
  } = formState;

  /**
   * Function to send the information to the api
   */
  const handleSend = () => {
    const action = foodId ? `handle-food?foodId=${foodId}` : `handle-food`;

    const { convertedValue, convertedUnit } = convertMeasurement(
      measurementsValue,
      unitOfMeasure
    );

    const bodyToSend = {
      ...formState,
      measurementsValue: convertedValue,
      unitOfMeasure: convertedUnit,
    };

    submit(`/actions/meal-planner/${action}`, {
      method: "POST",
      body: bodyToSend,
    });
  };

  /**
   * Function to not allow "e" "+" and + "-" characters
   * @param {*} e
   */
  const handleKeyDown = (e) => {
    if (["e", "E", "-", "+"].includes(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Validate the quantity input and set quantityNumber and quantityDescription
   * @param {*} e
   */
  const handleQuantityInputChange = (e) => {
    const value = e.target.value;

    setFormState((prevState) => ({
      ...prevState,
      fullQuantity: value,
    }));

    // Validate the format: number (int, fraction, or decimal) followed by space and text
    const match = value.trim().match(regex);

    if (match) {
      const [, number, description] = match;
      setFormState((prevState) => ({
        ...prevState,
        quantityNumber: number, // Extracted number
        quantityDescription: description, // Extracted description
      }));
      setErrorQtyMessage("");
    } else {
      // Update the input with empty values if it doesn't match the format
      setFormState((prevState) => ({
        ...prevState,
        quantityNumber: undefined,
        quantityDescription: "",
      }));
    }
  };

  /**
   * Show an error message if the input value is invalid
   */
  const handleQuantityBlur = () => {
    const { fullQuantity } = formState;

    if (!regex.test(fullQuantity.trim())) {
      setErrorQtyMessage(
        "You must enter a valid number followed by text (e.g., '1 banana')."
      );
    } else {
      setErrorQtyMessage("");
    }
  };

  /**
   * Select the unit of measure
   * @param {*} newVal
   * @param {*} name
   */
  const handleSelectChange = (newVal) => {
    setFormState((prevState) => ({
      ...prevState,
      unitOfMeasure: newVal,
    }));
  };

  /**
   * Save the inputs values
   * @param {*} e
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /**
   * Function to validate if the form is valid
   * @returns
   */
  const isFormValid = () => {
    return (
      name &&
      fullQuantity &&
      measurementsValue &&
      quantityNumber !== undefined &&
      quantityDescription.trim() !== ""
    );
  };

  /**
   * Update the calories input everytime proteins, carbs or fats is affected
   */
  useEffect(() => {
    if (proteins === 0 && carbs === 0 && fats === 0) return;
    const proteinsKcal = customRound(proteins * 4);
    const carbsKcal = customRound(carbs * 4);
    const fatsKcal = customRound(fats * 9);
    const totalCalories = customRound(proteinsKcal + carbsKcal + fatsKcal);

    setFormState((prevState) => ({
      ...prevState,
      calories: totalCalories,
    }));
  }, [proteins, carbs, fats]);

  return (
    <Modal
      className="edit-food-modal"
      title={title}
      toClear={["foodId"]}
      backButton={true}
    >
      <div className="body">
        <div className="inputs">
          <div>
            <label className="label" htmlFor="name">
              Food Name
            </label>
            <input
              type="text"
              placeholder={`Enter food name`}
              name="name"
              value={name}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="label" htmlFor="brand">
              Brand Name
            </label>
            <input
              type="text"
              placeholder={`Enter brand name`}
              name="brand"
              value={brand}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="label" htmlFor="quantity">
              Quantity
            </label>
            <input
              type="text"
              placeholder={`A number and unit e.g. 1 slice, 2 pcs, etc.`}
              name="quantity"
              value={fullQuantity}
              onChange={handleQuantityInputChange}
              onBlur={handleQuantityBlur}
            />
            {errorQtyMessage && (
              <span className="text-[--danger] mt-1 text-[0.6875rem]">
                {errorQtyMessage}
              </span>
            )}
          </div>
          <div>
            <label className="label" htmlFor="measurementsValue">
              Measurements
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                placeholder={`Enter measurements`}
                name="measurementsValue"
                value={measurementsValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <Select
                name="unitOfMeasure"
                value={unitOfMeasure}
                onChange={(newVal) => handleSelectChange(newVal)}
                options={unitOptionsToUse.map((elem) => {
                  return { value: elem, text: elem };
                })}
              />
            </div>
          </div>
        </div>
        <div className="nutrition">
          <div>
            <label className="label protein" htmlFor="proteins">
              Protein
            </label>
            <div>
              <input
                type="number"
                name="proteins"
                value={proteins}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <span>g</span>
            </div>
          </div>
          <div>
            <label className="label carbs" htmlFor="carbs">
              Carbs
            </label>
            <div>
              <input
                name="carbs"
                type="number"
                value={carbs}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <span>g</span>
            </div>
          </div>
          <div>
            <label className="label fat" htmlFor="fats">
              Fat
            </label>
            <div>
              <input
                type="number"
                name="fats"
                value={fats}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <span>g</span>
            </div>
          </div>
          <div>
            <label className="label text-[#10c268]" htmlFor="calories">
              kCal
            </label>
            <div>
              <input
                type="number"
                name="calories"
                value={calories}
                disabled={true}
              />
            </div>
          </div>
        </div>
        <div className="footer">
          <button
            data-button-type="primary"
            disabled={!isFormValid()}
            onClick={handleSend}
          >
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
};

HandleFoodModal.propTypes = {
  locals: PropTypes.object,
};
